import { useCallback, useMemo } from 'react';
import { AppWindow } from '@phosphor-icons/react';
import { createVSCodeUri } from 'common/commonUtil';
import { useError } from 'client/lib/Error';
import { useApi } from 'client/lib/trpc';
import { useUser } from 'client/lib/User';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { type DropdownMenuItemProps } from 'client/components/DropdownMenu';
import cursorLogo from 'client/static/image/cursor.webp';
import vscodeLogo from 'client/static/image/vscode-alt.svg';

interface DevelopButton {
  name: string;
  title: string;
  description?: string;
  icon: JSX.Element;
  href?: string;
  onClick?: () => void;
}

export const useDevelopButtons = () => {
  const { user } = useUser();
  const deploymentID = useDeploymentClientState((state) => state.document?.id);
  const sshUsernames = useDeploymentClientState((state) => state.document?.sshUsernames || []);
  const { setError } = useError({ showSnackbar: true });
  const api = useApi();

  const pinnedEditorIDs = user?.pinnedEditorIDs || ['browser'];

  // Find devpod entry ssh connect string
  const devpodSshConnectString =
    sshUsernames.find((s) => s.namespaceName == '' && s.podName == 'dev' && s.containerName == '')?.sshConnectString || '--';

  const handleVsCodeBrowserIdeClick = useCallback(async () => {
    if (!deploymentID) return;

    try {
      const response = await api.token.codeEditorAuth.query({ deploymentID, identifier: 'code' });
      if (response.status === 'ok') {
        window.open(response.url, '_blank')?.focus();
      } else {
        console.error('handleVsCodeBrowserIdeClick error', JSON.stringify(response));
        setError('Failed to open Browser IDE. Please try again…', { status: response.status, errorKey: 'browser-ide' });
      }
    } catch (error) {
      console.error('handleVsCodeBrowserIdeClick error', JSON.stringify(error));
      setError('Failed to open Browser IDE. Please try again…', { status: 'network error', errorKey: 'browser-ide' });
    }
  }, [deploymentID]);

  const developButtons = useMemo<{ [id: string]: DevelopButton }>(
    () => ({
      browser: {
        name: 'Browser IDE',
        title: 'Code in the Browser',
        icon: <AppWindow size={22} />,
        onClick: handleVsCodeBrowserIdeClick,
      },
      vscode: {
        name: 'VS Code',
        title: 'Open in VS Code',
        description: 'Via SSH remote',
        icon: <img src={vscodeLogo} width={18} height={18} />,
        href: createVSCodeUri({ sshUrl: devpodSshConnectString, path: '/app' }),
      },
      cursor: {
        name: 'Cursor',
        title: 'Open in Cursor',
        description: 'Via SSH remote',
        icon: <img src={cursorLogo} width={22} height={22} />,
        href: createVSCodeUri({ protocol: 'cursor', sshUrl: devpodSshConnectString, path: '/app' }),
      },
    }),
    [devpodSshConnectString, handleVsCodeBrowserIdeClick]
  );

  const menuItems = useMemo<DropdownMenuItemProps[]>(
    () =>
      Object.entries(developButtons).map(([id, entry]) => ({
        id,
        'data-name': entry.name,
        title: entry.title,
        subtitle: entry.description,
        icon: entry.icon,
        href: entry.href,
        external: true,
        target: '_self',
        onClick: entry.onClick,
      })),
    [pinnedEditorIDs, developButtons]
  );

  const pinnedEditors = useMemo(
    () => menuItems.filter((item) => pinnedEditorIDs.includes(item.id)),
    [menuItems, pinnedEditorIDs.join(',')]
  );

  return { menuItems, developButtons, pinnedEditors };
};
